import { ChevronDownIcon } from '@radix-ui/react-icons';
import { Select as SelectPrimitive } from 'radix-ui';
import React, { forwardRef } from 'react';

import { cn } from '~/utils/styles';

interface SelectProps {
  value?: string;
  onValueChange?: (value: string) => void;
  children: React.ReactNode;
  label?: string;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
}

interface SelectItemProps {
  value: string;
  children: React.ReactNode;
  disabled?: boolean;
}

const Select = forwardRef<HTMLDivElement, SelectProps>(
  ({ value, onValueChange, children, label, disabled, className, placeholder }, ref) => {
    return (
      <div ref={ref} className="flex flex-col space-y-2">
        {label && <label className="text-sm font-medium text-gray-700">{label}</label>}
        <SelectPrimitive.Root value={value} onValueChange={onValueChange} disabled={disabled}>
          <SelectPrimitive.Trigger
            className={cn(
              'flex h-10 w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-white placeholder:text-gray-500 focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:outline-hidden disabled:cursor-not-allowed disabled:opacity-50',
              className,
            )}
          >
            <SelectPrimitive.Value placeholder={placeholder} />
            <SelectPrimitive.Icon className="h-4 w-4 opacity-50">
              <ChevronDownIcon />
            </SelectPrimitive.Icon>
          </SelectPrimitive.Trigger>
          <SelectPrimitive.Portal>
            <SelectPrimitive.Content className="relative z-50 max-h-[85vh] min-w-32 overflow-y-auto rounded-md border bg-white text-gray-900 shadow-md data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95">
              <SelectPrimitive.Viewport className="p-1">{children}</SelectPrimitive.Viewport>
            </SelectPrimitive.Content>
          </SelectPrimitive.Portal>
        </SelectPrimitive.Root>
      </div>
    );
  },
);

const SelectItem = forwardRef<React.ElementRef<typeof SelectPrimitive.Item>, SelectItemProps>(
  ({ value, children, disabled }, ref) => {
    return (
      <SelectPrimitive.Item
        ref={ref}
        value={value}
        disabled={disabled}
        className="relative flex w-full cursor-default items-center rounded-xs py-1.5 pr-2 pl-8 text-sm outline-hidden select-none focus:bg-gray-100 focus:text-gray-900 data-disabled:pointer-events-none data-disabled:opacity-50"
      >
        <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
          <SelectPrimitive.ItemIndicator>
            <div className="h-1.5 w-1.5 rounded-full bg-current" />
          </SelectPrimitive.ItemIndicator>
        </span>
        <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
      </SelectPrimitive.Item>
    );
  },
);

Select.displayName = 'Select';
SelectItem.displayName = 'SelectItem';

export { Select, SelectItem };
